import React, { FC, useCallback, useMemo, MouseEvent, useRef } from "react";
import { Avatar } from "antd";
import {
  getAccessibleTextColor,
  primaryColor,
} from "../../../../../common/utilities/color";
import EditIcon from "../../../../../common/iconComponents/EditIcon";
import { useAppDispatch, useAppSelector } from "../../../../../app";
import { useActiveDocumentSet } from "../../../../../documentSet";
import useScroll from "../../../../../configMap/hooks/useScroll";
import { GroupBlockEntityType } from "../../../../../configMap";
import {
  changeActiveEntity,
  changeAnnotating,
} from "../../../../../app/store/appSlice";
import isErrorDisabledSelector from "../../../../../configMap/selectors/isErrorDisabledSelector";

type Props = {
  isLoading?: boolean;
  groupBlockEntityType: GroupBlockEntityType;
  groupBlockIndex: number;
  disabled: boolean;
};

const EntityHotKey: FC<Props> = ({
  isLoading = false,
  groupBlockEntityType,
  groupBlockIndex,
  disabled,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useScroll({
    ref: scrollRef,
    groupBlockIndex,
    entity: groupBlockEntityType,
  });

  const dispatch = useAppDispatch();
  const { isAnnotating, isEditing, activeEntity } = useAppSelector(
    (state) => state.appReducer,
  );

  const { activeDocumentSet } = useActiveDocumentSet();

  const { disabledEntities } = useAppSelector(
    (state) => state.configMapReducer,
  );

  const isErrorDisabled = useAppSelector(isErrorDisabledSelector);

  const colorToUse = useMemo(() => {
    const { color } = groupBlockEntityType;
    if (!color) {
      return primaryColor;
    }

    if (
      isErrorDisabled ||
      groupBlockEntityType.entityType.isComputed ||
      disabledEntities.includes(groupBlockEntityType.entityType.id)
    ) {
      return "#bebebe";
    }

    if (activeEntity && isAnnotating) {
      if (
        activeEntity.entityType.id === groupBlockEntityType.entityType.id &&
        groupBlockIndex === activeEntity.index
      ) {
        return color;
      } else {
        return "#bebebe";
      }
    }

    return color;
  }, [
    activeEntity,
    isAnnotating,
    disabledEntities,
    groupBlockEntityType,
    groupBlockIndex,
    isErrorDisabled,
  ]);

  const cursor = useMemo(() => {
    if (
      disabledEntities.includes(groupBlockEntityType.entityType.id) ||
      groupBlockEntityType.entityType.isComputed ||
      isErrorDisabled
    ) {
      return "not-allowed";
    }

    if (disabled) {
      return "default";
    }

    return "pointer";
  }, [
    disabled,
    groupBlockEntityType.entityType,
    disabledEntities,
    isErrorDisabled,
  ]);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      if (!activeDocumentSet) {
        return;
      }

      if (isEditing) {
        return;
      }

      if (disabled) {
        return;
      }

      if (isErrorDisabled) {
        return;
      }

      if (disabledEntities.includes(groupBlockEntityType.entityType.id)) {
        return;
      }

      if (groupBlockEntityType.entityType.isComputed) {
        return;
      }

      if (
        isAnnotating &&
        activeEntity?.entityType.id === groupBlockEntityType.entityType.id &&
        activeEntity?.index === groupBlockIndex
      ) {
        dispatch(changeAnnotating(false));
        dispatch(changeActiveEntity(undefined));
      } else {
        dispatch(changeAnnotating(true));
        dispatch(
          changeActiveEntity({
            ...groupBlockEntityType,
            index: groupBlockIndex,
          }),
        );
      }
    },
    [
      disabled,
      activeDocumentSet,
      isEditing,
      isAnnotating,
      groupBlockEntityType,
      groupBlockIndex,
      dispatch,
      activeEntity?.entityType.id,
      activeEntity?.index,
      disabledEntities,
      isErrorDisabled,
    ],
  );

  if (isLoading) {
    return (
      <div
        id={`${groupBlockEntityType.entityType.id}-${groupBlockIndex}`}
        ref={scrollRef}
        onClick={handleClick}
      />
    );
  }

  if (
    groupBlockEntityType.hotkey &&
    !groupBlockEntityType.entityType.isComputed
  ) {
    return (
      <div
        id={`${groupBlockEntityType.entityType.id}-${groupBlockIndex}`}
        ref={scrollRef}
        onClick={handleClick}
      >
        <Avatar
          size={25}
          style={{
            backgroundColor: colorToUse,
            color: getAccessibleTextColor(colorToUse),
            width: 20,
            cursor,
            borderRadius: 3,
          }}
          shape="square"
        >
          {groupBlockEntityType.hotkey.toUpperCase()}
        </Avatar>
      </div>
    );
  }

  return (
    <Avatar
      size={25}
      style={{
        backgroundColor: colorToUse,
        width: 20,
        borderRadius: 3,
        cursor,
      }}
      shape="square"
      icon={
        groupBlockEntityType.entityType.isComputed ? (
          <span
            className="bi bi-cpu"
            style={{ color: getAccessibleTextColor(colorToUse), fontSize: 12 }}
          />
        ) : (
          <EditIcon fill={getAccessibleTextColor(colorToUse)} />
        )
      }
    />
  );
};

export default EntityHotKey;
